/**
 * SERVICIO DE EMAIL CON RENDER
 * 
 * Este módulo proporciona funcionalidades para enviar emails usando la API de Render.
 * Incluye funcionalidades para enviar notificaciones de asignación de tests a candidatos.
 * 
 * Características principales:
 * - Envío de emails através de API de Render
 * - Templates personalizados para asignación de tests
 * - Manejo de errores
 * - Autenticación con tokens JWT
 */

// URL base para el endpoint de Render (ahora para credenciales y envío)
const RENDER_API_URL = process.env.REACT_APP_API_BASE_URL_RENDER;

/**
 * Servicio de email que maneja operaciones relacionadas con el envío de correos electrónicos
 * y la creación de credenciales temporales a través de la API de Render.
 */
const emailService = {
    /**
     * Obtiene el token de autenticación desde localStorage
     * @returns {string|null} Token de acceso o null si no existe
     */
    getAuthToken() {
        return localStorage.getItem('access_token');
    },

    /**
     * Obtiene los headers necesarios para las peticiones autenticadas
     * @returns {Object} Headers con autenticación
     */
    getAuthHeaders() {
        const token = this.getAuthToken();
        return {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
        };
    },

    /**
     * Crea credenciales temporales y envía un email de asignación de test al candidato.
     * Esta función interactúa con un endpoint de backend que maneja la generación de credenciales
     * y el envío del correo electrónico con el link de login.
     *
     * @param {Object} data - Datos para la creación de credenciales y envío de email.
     * @param {string} data.id_candidato - ID del candidato al que se le asignará el test.
     * @param {string} data.id_empresa - ID de la empresa a la que pertenece el candidato.
     * @param {string} data.test_url - URL completa a la que el candidato debe acceder para el test (login).
     * @param {string} data.test_name - Nombre del test que se está asignando (para el email).
     * @param {string} data.candidate_email - Email del candidato para el envío.
     * @param {string} data.candidate_full_name - Nombre completo del candidato (para el email).
     * @returns {Promise<Object>} Resultado de la operación del backend.
     * @throws {Object} Error con mensaje descriptivo si falla la creación/envío.
     *
     * @example
     * const result = await emailService.sendTestCredentials({
     *   id_candidato: 'some-candidate-id',
     *   id_empresa: 'some-company-id',
     *   test_url: 'https://example.com/login',
     *   test_name: 'Test de Lógica',
     *   candidate_email: 'candidato@mail.com',
     *   candidate_full_name: 'Juan Pérez'
     * });
     */
    async sendTestCredentials({ id_candidato, id_empresa, test_url, test_name, candidate_email, candidate_full_name }) {
        try {
            if (!RENDER_API_URL) {
                throw { message: 'URL de Render no configurada. Verifica REACT_APP_API_BASE_URL_RENDER' };
            }
            if (!id_candidato || !id_empresa || !test_url || !test_name || !candidate_email || !candidate_full_name) {
                throw { message: 'Faltan datos obligatorios para crear credenciales y enviar email.' };
            }

            const payload = {
                id_candidato,
                id_empresa,
                test_name,
                candidate_email,
                candidate_full_name
            };

            const response = await fetch(
                `${RENDER_API_URL}/api/v1/temp-credentials/create-and-send?test_url=${encodeURIComponent(test_url)}`,
                {
                    method: 'POST',
                    headers: this.getAuthHeaders(),
                    body: JSON.stringify(payload)
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw { message: result.detail || result.message || `Error ${response.status}: ${response.statusText}` };
            }

            // Devolver el resultado completo para que el llamador maneje el éxito/error.
            return result;

        } catch (error) {
            console.error('Error en sendTestCredentials:', error);
            throw error.message ? error : { message: 'Error en el servidor de credenciales.' };
        }
    },

    /**
     * Envía emails de asignación de test a múltiples candidatos.
     * Refactorizada para usar sendTestCredentials para cada candidato.
     *
     * @param {Array<Object>} candidates - Array de objetos de candidato (debe incluir id_candidato, email, nombre_completo, id_empresa).
     * @param {string} testName - Nombre del test.
     * @param {string} testLink - URL de login para el test.
     * @returns {Promise<Object>} Resultado de los envíos masivos.
     * @throws {Object} Error con mensaje descriptivo si falla.
     */
    async sendBulkTestAssignment(candidates, testName, testLink) {
        try {
            const results = {
                success: 0,
                errors: [],
                total: candidates.length,
                details: []
            };

            for (let i = 0; i < candidates.length; i++) {
                const candidate = candidates[i];
                try {
                    await this.sendTestCredentials({
                        id_candidato: candidate.id_candidato, // este es el id del canidato del destino 
                        id_empresa: candidate.id_empresa,  // este es el id de la empresa 
                        test_url: testLink,  // link 
                        test_name: testName,// Nombre del test 
                        candidate_email: candidate.email, // email destinatario
                        candidate_full_name: candidate.nombre_completo // nombre del candidato 
                    });
                    
                    results.success++;
                    results.details.push({
                        candidate: candidate.nombre_completo,
                        email: candidate.email,
                        status: 'success'
                    });

                    // Pequeña pausa entre envíos para evitar sobrecarga del servidor
                    await new Promise(resolve => setTimeout(resolve, 500));

                } catch (error) {
                    results.errors.push({
                        candidate: candidate.nombre_completo,
                        email: candidate.email,
                        error: error.message || 'Error desconocido'
                    });
                    
                    results.details.push({
                        candidate: candidate.nombre_completo,
                        email: candidate.email,
                        status: 'error',
                        error: error.message
                    });
                }
            }

            return results;

        } catch (error) {
            console.error('Error en envío masivo de emails:', error);
            throw error.message ? error : { message: 'Error en el servidor de email' };
        }
    },

    /**
     * Envía asignación de test a múltiples candidatos a través de un único endpoint de API.
     *
     * @param {Array<string>} idsCandidatos - Array de IDs de candidatos.
     * @param {string} idEmpresa - ID de la empresa.
     * @param {string} testUrl - URL del test.
     * @returns {Promise<Object>} La respuesta del backend con el resumen de los envíos.
     * @throws {Object} Error con mensaje descriptivo si falla el envío masivo.
     */
    async sendMassTestAssignment(idsCandidatos, idEmpresa, testUrl) {
        try {
            if (!RENDER_API_URL) {
                throw { message: 'URL de Render no configurada. Verifica REACT_APP_API_BASE_URL_RENDER' };
            }
            if (!Array.isArray(idsCandidatos) || idsCandidatos.length === 0 || !idEmpresa || !testUrl) {
                throw { message: 'Faltan datos obligatorios para el envío masivo: IDs de candidatos, ID de empresa o URL del test.' };
            }

            const payload = {
                ids_candidatos: idsCandidatos,
                id_empresa: idEmpresa,
                test_url: testUrl
            };

            const response = await fetch(
                `${RENDER_API_URL}/api/temp-credentials/create-and-send`,  
                {
                    method: 'POST',
                    headers: this.getAuthHeaders(),
                    body: JSON.stringify(payload)
                }
            );

            const result = await response.json();

            if (!response.ok) {
                throw { message: result.detail || result.message || `Error ${response.status}: ${response.statusText}` };
            }

            // Asumiendo que el backend siempre devolverá la estructura de éxito/fallo.
            // Si no, se podría añadir una validación de la estructura de `result` aquí.
            return result;

        } catch (error) {
            console.error('Error en sendMassTestAssignment:', error);
            throw error.message ? error : { message: 'Error en el servidor de envío masivo de emails.' };
        }
    },

    /**
     * Envía un email personalizado (mantenida por si acaso, aunque el flujo principal es con credenciales).
     * @param {Object} emailData - Datos del email
     * @param {string} emailData.to - Email del destinatario
     * @param {string} emailData.subject - Asunto del email
     * @param {string} emailData.html - Contenido HTML del email
     * @returns {Promise<Object>} Resultado del envío
     * @throws {Object} Error con mensaje descriptivo si falla el envío
     */
    async sendCustomEmail({ to, subject, html }) {
        try {
            if (!RENDER_API_URL) {
                throw { message: 'URL de Render no configurada. Verifica REACT_APP_API_BASE_URL_RENDER' };
            }

            if (!to || !subject || !html) {
                throw { message: 'Faltan datos obligatorios: destinatario, asunto y contenido' };
            }

            const emailPayload = { to, subject, html };

            const response = await fetch(`${RENDER_API_URL}/api/send-email`, {
                method: 'POST',
                headers: this.getAuthHeaders(),
                body: JSON.stringify(emailPayload)
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => ({ message: 'Error en el servidor' }));
                throw { message: errorData.message || `Error ${response.status}: ${response.statusText}` };
            }

            const result = await response.json();
            return {
                success: true,
                message: 'Email enviado exitosamente',
                data: result
            };

        } catch (error) {
            console.error('Error al enviar email personalizado:', error);
            throw error.message ? error : { message: 'Error en el servidor de email' };
        }
    }
};

export default emailService; 